/**
 * omnyqra: UI language (Omnyqra/sim#43), see FORK.md.
 *
 * The locale is never part of the URL. It comes from the `NEXT_LOCALE` cookie
 * (set by the language picker in settings), else from `Accept-Language`, else
 * English, which is also the source language of every message.
 */
export const LOCALES = ['en', 'de'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

/**
 * One JSON file per area under `messages/<locale>/`, the file name is the
 * namespace. `check:i18n-messages` fails when `NAMESPACES` and the files differ.
 * `i18n/request.ts` loads the UI namespaces only; of those the root layout
 * hands `ROOT_NAMESPACES` to every page and the rest below the settings
 * layouts (Omnyqra/sim#51).
 */
export const UI_NAMESPACES = [
  'auth',
  'settings',
  'errors',
  'invite',
  'unsubscribe',
  'upgrade',
  'interfaces',
  'team',
  'billing',
  'keys',
  'tools',
  'accessControl',
  'workspace',
  'sidebar',
  'logs',
  'tables',
  'common',
  'knowledge',
  'files',
  'integrations',
  'skills',
  'blockNames',
  'editor',
  'blockEditor',
  'deploy',
  'terminal',
  'dashboards',
  'changelog',
  'workflowTests',
] as const

/**
 * omnyqra (Omnyqra/sim#55): UI namespaces only the workflow editor renders.
 * `AllMessagesProvider` leaves them out; `app/workspace/[workspaceId]/w/layout.tsx`
 * (editor and workflow index) adds them on top of
 * the workspace's messages (`i18n/editor-messages-provider.tsx`). The other
 * editor namespaces stay in `AllMessagesProvider` because previews in logs and
 * tables and the shared sub-block inputs (MCP settings, knowledge, custom
 * tools) render them outside the editor.
 */
export const EDITOR_NAMESPACES = [
  'deploy',
] as const satisfies readonly (typeof UI_NAMESPACES)[number][]

/** Rendered on the server only (Omnyqra/sim#50), see `components/emails/i18n.ts`. */
const SERVER_NAMESPACES = ['emails'] as const

/**
 * omnyqra (Omnyqra/sim#56): a folder of per-block files, not one file. Not a
 * `next-intl` namespace: the workflow editor layout loads it for the current
 * language (`i18n/block-texts-server.ts`) and hands it down through
 * `BlockTextsProvider`, so no other page carries it.
 */
export const FOLDER_NAMESPACES = ['blocks'] as const

export const NAMESPACES = [...UI_NAMESPACES, ...SERVER_NAMESPACES, ...FOLDER_NAMESPACES] as const

/**
 * omnyqra (Omnyqra/sim#51): namespaces the root layout hands to every client
 * component. The others reach the client only below `AllMessagesProvider`.
 * `upgrade` stays here because the landing pricing page renders its toggle,
 * `interfaces` because published chats render outside every settings layout.
 * `common` holds the emcn default texts every page needs (Omnyqra/sim#67).
 */
export const ROOT_NAMESPACES = [
  'auth',
  'errors',
  'invite',
  'unsubscribe',
  'upgrade',
  'interfaces',
  'common',
] as const satisfies readonly (typeof UI_NAMESPACES)[number][]

/**
 * omnyqra (Omnyqra/sim#56): block texts, read with `useBlockText`/`useBlockName`
 * as plain strings, not ICU messages: they contain `{…}` in JSON and template
 * placeholders.
 */
export const RAW_NAMESPACES = ['blockNames', 'blocks'] as const

export const LOCALE_COOKIE = 'NEXT_LOCALE'

/** Each language names itself, whatever the current UI language. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  de: 'Deutsch',
}

/** One year, the picker is a standing preference. */
export const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * Supported locale with the highest `q` in an `Accept-Language` header.
 * Regional tags match their base language (`de-AT` → `de`); `q=0` and
 * malformed weights are skipped.
 */
export function matchAcceptLanguage(header: string): Locale | null {
  let best: { locale: Locale; q: number } | null = null
  for (const entry of header.split(',')) {
    const [tag, ...params] = entry.trim().split(';')
    const base = tag.trim().split('-')[0].toLowerCase()
    if (!isLocale(base)) continue

    let q = 1
    const qParam = params.map((p) => p.trim()).find((p) => p.startsWith('q='))
    if (qParam) {
      q = Number(qParam.slice(2))
      if (!Number.isFinite(q) || q < 0 || q > 1) continue
    }
    if (q === 0) continue
    if (!best || q > best.q) best = { locale: base, q }
  }
  return best?.locale ?? null
}

export function resolveLocale(
  cookieValue: string | undefined,
  acceptLanguage: string | null | undefined
): Locale {
  if (isLocale(cookieValue)) return cookieValue
  if (acceptLanguage) {
    const matched = matchAcceptLanguage(acceptLanguage)
    if (matched) return matched
  }
  return DEFAULT_LOCALE
}
